/**
 * Màn **Cấu trúc prompt** — nơi `Chốt` của G7 được nghiệm thu:
 * *"Lynust chỉnh được prompt mà không cần code."*
 *
 * Bốn khối chỉnh được, cộng một khối theo dõi. Thứ tự khớp thứ tự AI đọc.
 *
 * ⚠️ **Hồ sơ site KHÔNG còn ở đây.** Câu *"site này viết về cái gì"* đã chuyển sang màn Chân
 * dung khách — nó trả lời cùng một câu hỏi với chân dung người đọc (*viết cho ai, về cái gì*),
 * còn số bài mỗi kỳ chuyển sang Cài đặt theo `CLAUDE.md` §5.18. Đặt một màn cấu hình site vào
 * đây rồi dán nhãn "cấu trúc prompt" là gọi tên sai một thứ, và màn này khi đó **không có việc
 * gì để làm** — đó chính là điều Lynust chỉ ra ngày 09/09/2026.
 *
 * ⚠️ **Sửa ở đây chỉ đổi cho bài viết SAU lúc sửa.** Mọi giá trị được đọc một lần, ngay đầu job
 * `tao-prompt`. Bài đã viết, bài đang chờ duyệt, bài đã lên lịch — không đụng tới. Không có nút
 * "áp dụng lại", và đó là chủ ý: áp lại một cấu hình mới lên bài cũ nghĩa là gọi model lần nữa
 * cho một bài đã trả tiền rồi.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type BanGhiPrompt, type CauTrucPromptTraVe } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { gioVN } from "../lib/nhan.ts";
import {
  Bao,
  Chip,
  DangTai,
  DanhSach,
  DauThe,
  DauTrang,
  Doc,
  Hang,
  CongTac,
  Khoi,
  LuoiDoc,
  Nut,
  Rong,
  The,
  lopO,
} from "../thanh-phan/giao-dien.tsx";
import { useToast } from "../thanh-phan/toast.tsx";

export function CauTrucPrompt() {
  const qc = useQueryClient();
  const toast = useToast();
  const [moBai, datMoBai] = useState<string | null>(null);

  const ct = useQuery({ queryKey: khoa.cauTrucPrompt, queryFn: api.cauTrucPrompt });
  const banGhi = useQuery({ queryKey: khoa.banGhiPrompt, queryFn: api.banGhiPrompt });
  const chiTiet = useQuery({
    queryKey: [...khoa.banGhiPrompt, moBai],
    queryFn: () => api.banGhiCuaBai(moBai as string),
    enabled: moBai !== null,
  });

  const luu = useMutation({
    mutationFn: (v: { khoa: string; giaTri: unknown }) => api.datCaiDat(v.khoa, v.giaTri),
    onSuccess: (_kq, v) => {
      // Màn này dài và chia làm sáu khối; khối vừa lưu thường nằm giữa hoặc cuối trang. Toast là
      // chỗ duy nhất chắc chắn nằm trong tầm mắt tại thời điểm bấm.
      toast.on("Đã lưu", v.khoa);
      void qc.invalidateQueries({ queryKey: khoa.cauTrucPrompt });
      void qc.invalidateQueries({ queryKey: khoa.caiDat });
    },
    onError: (loi: Error, v) => toast.loi(`Không lưu được ${v.khoa}`, loi.message),
  });

  if (ct.error) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Cấu trúc prompt" />
        <Bao muc="loi" tieuDe="Không tải được">
          {ct.error.message}
        </Bao>
      </div>
    );
  }
  if (ct.isPending) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Cấu trúc prompt" />
        <DangTai />
      </div>
    );
  }

  const d: CauTrucPromptTraVe = ct.data;
  const soTat = d.nguon.filter((n) => n.tatDuoc && !n.dangBat).length;
  const soTatDuoc = d.nguon.filter((n) => n.tatDuoc).length;

  return (
    <div className="min-w-0 space-y-5">
      <DauTrang
        tieuDe="Cấu trúc prompt bài viết"
        moTa="Mọi thứ AI đọc trước khi ra đề bài."
        chiTiet="Sửa ở đây đổi cho mọi bài SAU đó — bài đã viết giữ nguyên đề bài của chính nó, và PromptRecord của chúng cũng vậy. Đó là lý do so sánh được hai bài viết ở hai thời điểm cấu hình khác nhau."
      />

      <MucTieu d={d} luu={luu} />
      <PhongCach d={d} luu={luu} />

      {/* ── Nguồn ── */}
      <Khoi
        nhan="Nguồn đổ vào prompt"
        id="prompt-nguon"
        moSan
        tomTat={
          soTat > 0 ? (
            <span className="text-canh-600">
              {soTat}/{soTatDuoc} nguồn đang tắt
            </span>
          ) : (
            `${d.nguon.length} nguồn, đủ cả`
          )
        }
      >
        <p className="text-xs text-muc-600">
          Theo đúng thứ tự AI đọc. Tắt một nguồn là bài sau <strong>không còn nhìn thấy nó</strong>{" "}
          — nhãn tương ứng biến mất khỏi đề bài, không phải chỉ mờ đi.
        </p>

        <div className="mt-2 divide-y divide-muc-100">
          {d.nguon.map((n) => (
            <div key={n.khoa} className="flex items-start justify-between gap-4 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-muc-900">{n.nhan}</p>
                <p className="text-xs text-muc-600">{n.moTa}</p>
                <p className="mt-0.5 text-2xs text-muc-500">{n.tuDau}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <code className="inline-block rounded bg-muc-100 px-1 py-0.5 text-2xs text-muc-600">
                    {n.nhanTrongDeBai}
                  </code>
                  {n.chiKhi && (
                    <Chip muc="thuong" title="Nguồn có điều kiện — vắng mặt không phải lỗi">
                      {n.chiKhi}
                    </Chip>
                  )}
                </div>
              </div>
              <div className="shrink-0 pt-1">
                {n.tatDuoc && n.khoaCaiDat ? (
                  <CongTac
                    bat={n.dangBat}
                    // Chỉ công tắc vừa gạt mới báo đang chạy — `isPending` một mình khoá cả sáu
                    // công tắc cùng lúc, và màn trông như bị treo.
                    dangChay={luu.isPending && luu.variables?.khoa === n.khoaCaiDat}
                    nhanDocMan={n.nhan}
                    hienChu={false}
                    co="nho"
                    doi={(ke) => luu.mutate({ khoa: n.khoaCaiDat as string, giaTri: ke })}
                  />
                ) : (
                  <span
                    className="text-2xs text-muc-500"
                    title="Tắt nguồn này thì lời gọi không còn ý nghĩa — nên nó không có khoá cài đặt nào cả"
                  >
                    bắt buộc
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Khoi>

      <Nguong d={d} luu={luu} />

      {/* ── Ba thứ màn này KHÔNG đổi được ── */}
      <Khoi nhan="Màn hình này không đổi được ba thứ" id="prompt-khong-doi">
        <p className="text-xs text-muc-600">
          Ghi ở đây để anh gặp lại nó mỗi lần đọc màn hình này, thay vì phải nhớ có một file nằm
          đâu đó. Ba thứ dưới đây phải sửa code mới đổi được — nhắn tôi khi cần.
        </p>
        <ol className="mt-3 space-y-2 text-xs text-muc-700">
          <li>
            <span className="font-medium text-muc-900">1. Câu chữ bên trong mỗi nguồn.</span> Công
            tắc trên kia bật/tắt được cả một nguồn, nhưng không sửa được câu chữ trong nó. Ví dụ
            luật cấm bịa đang liệt kê đúng sáu mục, và cả sáu là ràng buộc pháp lý chứ không phải
            lựa chọn văn phong.
          </li>
          <li>
            <span className="font-medium text-muc-900">2. Cách đặt tên chủ đề.</span> Bài từ
            catalogue hiện lấy tên sản phẩm thật; bài research lấy chủ đề từ báo cáo. Không có chỗ
            nào chọn góc nhìn theo chân dung khách hay theo bài đang chạy tốt.
          </li>
          <li>
            <span className="font-medium text-muc-900">3. Ràng buộc song ngữ của company.</span>{" "}
            Thiếu bản tiếng Anh là <strong>400</strong> bên Website IME. Đó là dữ kiện của hệ đích,
            không phải lựa chọn — nên không có công tắc nào cho nó.
          </li>
        </ol>
        <p className="mt-3 text-xs text-muc-500">
          Câu <span className="text-muc-700">&ldquo;site này viết về cái gì&rdquo;</span> ở màn{" "}
          <span className="text-muc-700">Chân dung khách</span>. Số bài mỗi ngày hoặc mỗi tuần ở{" "}
          <span className="text-muc-700">Cài đặt → Nhịp đăng mỗi site</span>.
        </p>
      </Khoi>

      {/* ── Bản ghi ── */}
      <Khoi
        nhan="Prompt đã gửi"
        id="prompt-ban-ghi"
        tomTat={
          banGhi.data ? (
            banGhi.data.soBaiKhongCoBanGhi > 0 ? (
              <span className="text-canh-600">
                {banGhi.data.soBaiKhongCoBanGhi} bài không truy được
              </span>
            ) : (
              `${banGhi.data.bai.length} bài có bản ghi`
            )
          ) : (
            "…"
          )
        }
      >
        {banGhi.isPending ? (
          <DangTai />
        ) : banGhi.error ? (
          <Bao muc="loi">{banGhi.error.message}</Bao>
        ) : (
          <>
            <LuoiDoc cot={3}>
              <Doc nhan="bài có bản ghi" giaTri={banGhi.data.bai.length} />
              <Doc
                nhan="bài KHÔNG truy được"
                giaTri={banGhi.data.soBaiKhongCoBanGhi}
                sacThai={banGhi.data.soBaiKhongCoBanGhi > 0 ? "xau" : "tot"}
                ghiChu="viết trước khi có ghi chép"
              />
              <Doc
                nhan="tổng bản ghi"
                giaTri={banGhi.data.bai.reduce((t, b) => t + b.soBanGhi, 0)}
              />
            </LuoiDoc>

            {banGhi.data.soBaiKhongCoBanGhi > 0 && (
              <Bao muc="canh" tieuDe="Không bổ sung hồi tố được">
                {banGhi.data.soBaiKhongCoBanGhi} bài không có bản ghi prompt nào. Chúng viết trước
                khi có tính năng này, nên <strong>mãi mãi</strong> không trả lời được câu &ldquo;đề
                bài sai hay người viết sai&rdquo;. Không có cách sửa — chỉ có cách không để lặp lại.
              </Bao>
            )}

            {banGhi.data.bai.length === 0 ? (
              <Rong tieuDe="Chưa có bản ghi nào">
                Chạy dây chuyền một lượt thì bản ghi xuất hiện ở đây.
              </Rong>
            ) : (
              <DanhSach>
                {banGhi.data.bai.map((b) => (
                  <Hang key={b.articleId}>
                    <button
                      type="button"
                      onClick={() => datMoBai(moBai === b.articleId ? null : b.articleId)}
                      className="flex w-full min-w-0 items-center gap-2 px-4 py-2.5 text-left transition-colors duration-100 hover:bg-muc-100"
                    >
                      <span
                        className="min-w-0 flex-1 truncate text-sm text-muc-800"
                        title={b.tieuDe ?? ""}
                      >
                        {b.tieuDe ?? "(bài đã bị xoá)"}
                      </span>
                      {b.siteSlug && <Chip muc="thuong">{b.siteSlug}</Chip>}
                      <Chip muc="thuong">{b.soBanGhi} bản ghi</Chip>
                      <span className="shrink-0 text-2xs text-muc-500">
                        {moBai === b.articleId ? "đóng" : "xem"}
                      </span>
                    </button>

                    {moBai === b.articleId && (
                      <div className="border-t border-muc-200 bg-muc-50 px-4 py-3">
                        {chiTiet.isPending ? (
                          <DangTai />
                        ) : chiTiet.error ? (
                          <Bao muc="loi">{chiTiet.error.message}</Bao>
                        ) : (
                          <div className="space-y-4">
                            {chiTiet.data.banGhi.map((g: BanGhiPrompt) => (
                              <div key={g._id} className="min-w-0">
                                <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
                                  <Chip muc="dam">{g.tacVu}</Chip>
                                  <Chip muc="thuong">{g.catalogKey}</Chip>
                                  {g.nguonDaDung.map((n) => (
                                    <Chip key={n} muc="on">
                                      {n}
                                    </Chip>
                                  ))}
                                  <span className="ml-auto text-2xs text-muc-500">
                                    {gioVN(g.luc)}
                                  </span>
                                </div>

                                {/* NGUYÊN VĂN — không cắt. Một prompt cắt ở ký tự thứ 500 không
                                    nói được vì sao bài ra sai. */}
                                <p className="text-2xs font-medium uppercase tracking-[0.1em] text-muc-500">
                                  system
                                </p>
                                <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-tile bg-muc-0 p-2.5 text-2xs leading-relaxed text-muc-700 ring-1 ring-muc-200">
                                  {g.systemPrompt}
                                </pre>

                                <p className="mt-2 text-2xs font-medium uppercase tracking-[0.1em] text-muc-500">
                                  user
                                </p>
                                <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded-tile bg-muc-0 p-2.5 text-2xs leading-relaxed text-muc-700 ring-1 ring-muc-200">
                                  {g.userPrompt}
                                </pre>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </Hang>
                ))}
              </DanhSach>
            )}
          </>
        )}
      </Khoi>

      <p className="text-2xs text-muc-500">
        Chỉ <strong>AI tạo prompt</strong> đọc Bài học — AI viết bài không đọc. Nếu cả hai cùng đọc
        thì một luật được áp hai lần theo hai cách hiểu, và không ai truy được luật nào đã tác động.
      </p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Mục tiêu bài viết
 * ──────────────────────────────────────────────────────────────────────────── */

type Luu = ReturnType<typeof useMutation<unknown, Error, { khoa: string; giaTri: unknown }>>;

/**
 * Tỉ lệ mục tiêu: trong 10 bài thì bao nhiêu bài mỗi loại.
 *
 * **Không lưu theo từng phím gõ**, và **không tự co giãn cho tròn**. Lưu theo phím gõ biến số
 * `4` trên đường tới `10` thành một giá trị đã lưu; tự co giãn thì Lynust gõ một tỉ lệ và nhận
 * về một tỉ lệ khác.
 */
function MucTieu({ d, luu }: { d: CauTrucPromptTraVe; luu: Luu }) {
  const goc = () =>
    Object.fromEntries(d.mucTieu.ds.map((m) => [m.khoa, String(d.mucTieu.tiLe[m.khoa] ?? 0)]));
  const [ban, datBan] = useState<Record<string, string>>(goc);
  useEffect(() => datBan(goc()), [d.mucTieu.tiLe]);

  const so = d.mucTieu.ds.map((m) => Number(ban[m.khoa]));
  const dayDu = d.mucTieu.ds.every((m) => (ban[m.khoa] ?? "").trim() !== "");
  const hopLe =
    dayDu && so.every((n) => Number.isInteger(n) && n >= 0 && n <= d.mucTieu.tong);
  const tong = hopLe ? so.reduce((a, b) => a + b, 0) : null;
  const daDoi = d.mucTieu.ds.some((m) => Number(ban[m.khoa]) !== (d.mucTieu.tiLe[m.khoa] ?? 0));
  const luuDuoc = hopLe && tong === d.mucTieu.tong && daDoi && !luu.isPending;

  const gui = () =>
    luu.mutate({
      khoa: d.mucTieu.khoaCaiDat,
      giaTri: Object.fromEntries(d.mucTieu.ds.map((m) => [m.khoa, Number(ban[m.khoa])])),
    });

  return (
    <The>
      <DauThe tieuDe="Mục tiêu bài viết" />
      <p className="text-xs text-muc-600">
        Trong {d.mucTieu.tong} bài thì bao nhiêu bài mỗi loại. Mục tiêu được{" "}
        <strong>chọn sẵn trước khi gọi AI</strong>, nên tỉ lệ này là chắc chắn — không phải lời đề
        nghị để AI cân nhắc.
      </p>

      <div className="mt-3 space-y-3">
        {d.mucTieu.ds.map((m) => (
          <div key={m.khoa} className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm text-muc-900">{m.nhan}</p>
              <p className="text-xs text-muc-500">{m.moTa}</p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={d.mucTieu.tong}
                value={ban[m.khoa] ?? ""}
                disabled={luu.isPending}
                onChange={(e) => datBan((c) => ({ ...c, [m.khoa]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && luuDuoc) gui();
                  if (e.key === "Escape") datBan(goc());
                }}
                className={`${lopO} w-20 text-right so-thang-cot`}
              />
              <span className="w-10 text-xs text-muc-500">bài</span>
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 border-t border-muc-100 pt-3">
        <p className="text-xs">
          {!hopLe ? (
            <span className="text-loi-600">
              Mỗi ô phải là số nguyên từ 0 đến {d.mucTieu.tong}.
            </span>
          ) : tong === d.mucTieu.tong ? (
            <span className="text-muc-600">
              Tổng: <span className="so-thang-cot">{tong}</span>/{d.mucTieu.tong} ✓
            </span>
          ) : (
            <span className="text-loi-600">
              Tổng đang là <span className="so-thang-cot">{tong}</span>, phải bằng {d.mucTieu.tong}
              {tong !== null && tong < d.mucTieu.tong
                ? ` — còn thiếu ${d.mucTieu.tong - tong}`
                : tong !== null
                  ? ` — thừa ${tong - d.mucTieu.tong}`
                  : ""}
            </span>
          )}
        </p>
        {/*
          Cam **chỉ khi có gì để lưu**. Cam trong app này mang đúng một nghĩa — *có việc cần
          Lynust* — nên một nút Lưu cam thường trực trên màn không có thay đổi nào là cam nói dối.
          Ngược lại, lúc vừa gõ xong thì đúng một nút trên màn sáng lên, và đó là câu trả lời trực
          tiếp cho *"không biết bấm vào đâu"*.
        */}
        <Nut kieu={luuDuoc ? "chinh" : "vien"} disabled={!luuDuoc} dangChay={luu.isPending} onClick={gui}>
          Lưu tỉ lệ
        </Nut>
      </div>
    </The>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Phong cách viết
 * ──────────────────────────────────────────────────────────────────────────── */

function PhongCach({ d, luu }: { d: CauTrucPromptTraVe; luu: Luu }) {
  const p = d.phongCach;

  return (
    <The>
      <DauThe tieuDe="Phong cách viết" />
      <p className="text-xs text-muc-600">
        Mỗi bài một phong cách, xoay vòng <strong>lần lượt</strong> để phong cách nào cũng được số
        bài như nhau. Không phải chọn ngẫu nhiên — ở nhịp vài bài mỗi ngày, ngẫu nhiên cho ra phân
        bố lệch hẳn trong tuần đầu.
      </p>

      <div className="mt-3 space-y-3">
        <label className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muc-700">Chế độ</span>
          <select
            className={`${lopO} w-64`}
            value={p.tuDong ? "auto" : "chon"}
            disabled={luu.isPending}
            onChange={(e) =>
              luu.mutate({ khoa: p.khoaTuDong, giaTri: e.target.value === "auto" })
            }
          >
            <option value="auto">Tự động — xoay hết {p.ds.length} phong cách</option>
            <option value="chon">Tuỳ chọn — chỉ xoay những cái tôi tick</option>
          </select>
          <span className="text-xs text-muc-500">
            đang xoay {p.dangXoay.length}/{p.ds.length}
          </span>
        </label>

        {p.tuDong ? (
          <ul className="grid gap-1 sm:grid-cols-2">
            {p.ds.map((x) => (
              <li key={x.khoa} className="text-xs text-muc-600">
                <span className="font-medium text-muc-800">{x.nhan}</span> — {x.moTa}
              </li>
            ))}
          </ul>
        ) : (
          <div className="space-y-1.5 rounded-card border border-muc-200 bg-muc-50 p-3">
            {p.ds.map((x) => (
              <label key={x.khoa} className="flex cursor-pointer items-start gap-2.5 py-1">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-canh-500"
                  checked={x.dangXoay}
                  disabled={luu.isPending}
                  onChange={() => luu.mutate({ khoa: x.khoaCaiDat, giaTri: !x.dangXoay })}
                />
                <span className="min-w-0">
                  <span className="text-sm font-medium text-muc-900">{x.nhan}</span>
                  <span className="block text-xs text-muc-500">{x.moTa}</span>
                </span>
              </label>
            ))}
            {/* Không tick gì thì máy chủ xoay đủ tám thay vì ném — dây chuyền sản xuất không
                được chết vì một ô cấu hình trống. Nói thẳng ra, thay vì để nó là một bất ngờ. */}
            <p className="pt-1 text-2xs text-muc-500">
              Bỏ tick hết thì máy <strong>xoay lại đủ {p.ds.length} phong cách</strong> — phải còn
              ít nhất một phong cách để viết, và dây chuyền không dừng vì một ô trống.
            </p>
          </div>
        )}
      </div>
    </The>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Ngưỡng
 * ──────────────────────────────────────────────────────────────────────────── */

function Nguong({ d, luu }: { d: CauTrucPromptTraVe; luu: Luu }) {
  return (
    <The>
      <DauThe tieuDe="Ngưỡng" />
      <p className="text-xs text-muc-600">
        Năm con số điều khiển chất lượng và chi phí của mỗi lượt. Hai con số đầu{" "}
        <strong>cũng là ngưỡng của cửa kiểm</strong> — nâng số chữ tối thiểu lên là bài ngắn hơn bị
        chặn thật, không chỉ là lời dặn model.
      </p>
      <div className="mt-3 space-y-3">
        {d.nguong.map((n) => (
          <HangSo key={n.khoa} n={n} luu={luu} />
        ))}
      </div>
    </The>
  );
}

/**
 * Một dòng số.
 *
 * Giữ bản nháp cục bộ tới khi bấm Lưu, vì lưu theo từng phím gõ biến `35` trên đường tới `350`
 * thành một giá trị đã lưu — và với ngưỡng số chữ, giá trị đó đi thẳng vào cửa kiểm.
 */
function HangSo({ n, luu }: { n: CauTrucPromptTraVe["nguong"][number]; luu: Luu }) {
  const [ban, datBan] = useState(String(n.giaTri));
  useEffect(() => datBan(String(n.giaTri)), [n.giaTri]);

  const so = Number(ban);
  const hopLe = ban.trim() !== "" && Number.isInteger(so) && so >= n.toiThieu && so <= n.toiDa;
  const daDoi = so !== n.giaTri;
  const luuDuoc = hopLe && daDoi && !luu.isPending;

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm text-muc-900">
          {n.nhan}
          {!n.daDat && (
            <span className="ml-1.5 text-2xs text-muc-500" title="Chưa ai đặt — đang dùng mặc định">
              (mặc định)
            </span>
          )}
        </p>
        <p className="text-xs text-muc-500">{n.goiY}</p>
        {!hopLe && ban.trim() !== "" && (
          <p className="text-2xs text-loi-600">
            Phải là số nguyên từ {n.toiThieu} đến {n.toiDa}.
          </p>
        )}
      </div>
      <span className="inline-flex shrink-0 items-center gap-2">
        <input
          type="number"
          inputMode="numeric"
          min={n.toiThieu}
          max={n.toiDa}
          value={ban}
          disabled={luu.isPending}
          onChange={(e) => datBan(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && luuDuoc) luu.mutate({ khoa: n.khoa, giaTri: so });
            if (e.key === "Escape") datBan(String(n.giaTri));
          }}
          className={`${lopO} w-24 text-right so-thang-cot`}
        />
        <span className="w-12 text-xs text-muc-500">{n.donVi}</span>
        <Nut
          kieu={luuDuoc ? "chinh" : "vien"}
          disabled={!luuDuoc}
          // Chỉ ô vừa bấm mới quay — màn này có sáu ngưỡng, mỗi ngưỡng một nút Lưu.
          dangChay={luu.isPending && luu.variables?.khoa === n.khoa}
          onClick={() => luu.mutate({ khoa: n.khoa, giaTri: so })}
        >
          Lưu
        </Nut>
      </span>
    </div>
  );
}
